import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs, Button, Skeleton } from '@dreamtree-org/twreact-ui';
import { EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { shopHref } from '../lib/shop';
import { templateOf } from '../lib/design';
import { useStorefront } from '../StorefrontContext';
import { track } from '../lib/beacon';
import CatalogGate from '../components/CatalogGate';
import { isQtyItem } from '../lib/cart';
import { asVariant, choose, findVariant, initialSelection, unavailableText } from '../lib/variants';
import Region from '../design/Region';
import { ProductCtx, PRODUCT_SLOT_COMPONENTS, StickyBuyBar, buildProduct } from '../design/product';

/**
 * WEB-03 product detail + Digital Inspection Passport (mockup 03). The listing is
 * the EXACT inspected unit (C-02): its own gallery, grade, inspection checklist,
 * battery health with method and date, masked serial, seller identity and the
 * policy versions that apply. Detail resolves by id from the real catalog.
 * The layout is the owner's product template (D64): its blocks in order, with
 * the gallery style, spec tiles, breadcrumbs and sticky buy bar from its settings.
 */
export default function ProductDetail() {
  const { id } = useParams();
  const sf = useStorefront();
  const l = sf.byId(id);
  return (
    <CatalogGate skeleton={<Skeleton height={420} />}>
      {l ? (
        <Detail l={l} key={l.id} />
      ) : (
        <EmptyState title="This item is no longer available" action={<BrowseButton />}>
          It may have just been sold. Every listing is one physical unit, so a similar one is a better bet.
        </EmptyState>
      )}
    </CatalogGate>
  );
}

function BrowseButton() {
  const navigate = useNavigate();
  return <Button onClick={() => navigate('/shop')}>Browse the shop</Button>;
}

function Detail({ l }) {
  const sf = useStorefront();
  useEffect(() => track('product_view', { listingId: l.id }), [l.id]);
  const navigate = useNavigate();
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');
  const [qty, setQty] = useState(1);
  // The chosen variant (MERCH-87): its price, stock and photo; a listing without options has one.
  const [sel, setSelState] = useState(() => initialSelection(l));
  const variant = findVariant(l, sel);
  const lv = asVariant(l, variant);
  const setSel = (name, value) => {
    setSelState((s) => choose(l, s, name, value));
    setQty(1);
    setMsg(null);
  };
  const byQty = isQtyItem(lv);
  const insp = useQuery('sfInspection', { unit_id: l.unit_id }, { select: (r) => r.data });
  const seller = useQuery('sfSeller', { slug: sf.seller.slug, name: sf.seller.name }, { select: (r) => r.data });
  const tpl = templateOf(sf.doc, 'product');
  const type = sf.shop.typeOf(l);
  const section = type ? { label: type.plural, to: shopHref(type) } : { label: 'Shop', to: '/shop' };
  const inCart = sf.inCart(l.id, variant && variant.id != null ? variant.id : null);

  const add = async (thenCheckout) => {
    if (inCart) return navigate(thenCheckout ? '/checkout' : '/cart');
    const blocked = unavailableText(l, sel);
    if (blocked) return setMsg({ variant: 'warning', text: blocked });
    setBusy(thenCheckout ? 'buy' : 'add');
    const r = await sf.addToCart(lv, byQty ? qty : 1, variant);
    setBusy('');
    if (r.ok && thenCheckout) return navigate('/checkout');
    const added = byQty ? `Added ${qty} to your cart. They are held for you at checkout.` : 'Added to your cart. This exact unit is held for you at checkout.';
    setMsg(r.ok ? { variant: 'success', text: added } : { variant: 'error', text: r.error });
  };

  const ctx = buildProduct(sf, { l: lv, tpl, insp, seller, busy, msg, setMsg, add, qty, setQty, variant, sel, setSel });
  const layout = ctx.layout;

  return (
    <ProductCtx.Provider value={ctx}>
      <div className="space-y-5">
        {tpl.settings.breadcrumbs ? (
          <Breadcrumbs
            items={[
              { label: section.label, href: section.to, onClick: () => navigate(section.to) },
              { label: l.brand, href: `${section.to}?brand=${encodeURIComponent(l.brand)}`, onClick: () => navigate(`${section.to}?brand=${encodeURIComponent(l.brand)}`) },
              { label: [l.series, l.model_name].filter(Boolean).join(' ') },
            ]}
            onHomeClick={() => navigate('/')}
          />
        ) : null}
        <div className={layout.grid}>
          <Region blocks={tpl.blocks} slots={PRODUCT_SLOT_COMPONENTS} className={layout.full} />
        </div>
      </div>
      {tpl.settings.sticky_buy_bar ? <StickyBuyBar /> : null}
    </ProductCtx.Provider>
  );
}
